/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/*
 */

/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Row, Radio, Drawer, Empty } from "@duxton-web/ui"
import { ChevronLeftUniversal } from "@duxton-web/icons"

import { OverflowTip } from "components/common"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { GrabCategory } from "types/models/menu"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

import { drawerFooter, listSubCategory } from "../../styles"
import { LoadingComponent } from "../LoadingComponent"
import useSaveSelectedGrabCategory from "../useSaveSelectedGrabCategory"

interface SubDepartmentTaggingDrawerProps {
  visible: boolean
  onClose(): void
}

export const SubDepartmentTaggingDrawer: FC<SubDepartmentTaggingDrawerProps> = ({
  visible,
  onClose,
}: SubDepartmentTaggingDrawerProps) => {
  const {
    subDepartmentsForGrabCategory,
    isFetchingDataForGrabCategory,
    parentName,
  } = useSelector((state: AppState) => state.menu)

  const dispatch = useDispatch()

  const { isLoading: isRtkFlagLoading } = useRtkQueryMigrationEndpointsFlag()

  const [selectedCategory, setSelectedCategory] = useState<GrabCategory>()

  const saveSelectedGrabCategory = useSaveSelectedGrabCategory()

  const handleOnChangeSubCategory = (subDeptID: string) => {
    const selectedSubDepartment = subDepartmentsForGrabCategory?.find(
      subDept => subDept.ItemClassID === subDeptID,
    )

    setSelectedCategory({
      itemClassID: selectedSubDepartment?.ItemClassID as string,
      itemClassName: selectedSubDepartment?.Name as string,
      parentClassID: selectedSubDepartment?.ParentID as string,
      parentClassName: selectedSubDepartment?.ParentClassName,
    })
  }

  const handleSaveSubDepartmentDrawer = () =>
    saveSelectedGrabCategory(selectedCategory, {
      showDepartmentTaggingDrawer: false,
      showSubDepartmentTaggingDrawer: false,
    })

  const handleCancelSubDepartmentDrawer = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        showDepartmentTaggingDrawer: false,
        showSubDepartmentTaggingDrawer: false,
      }),
    )
  }

  return (
    <Drawer
      title={parentName}
      width={450}
      open={visible}
      onClose={onClose}
      destroyOnClose
      closeIcon={<ChevronLeftUniversal />}
    >
      {isFetchingDataForGrabCategory ? (
        <LoadingComponent data-testid="loading" />
      ) : (
        <div css={listSubCategory}>
          <Radio.Group
            onChange={(e: any) => {
              handleOnChangeSubCategory(e.target.value)
            }}
            data-testid="subcategory-list"
          >
            {subDepartmentsForGrabCategory?.map(subDept => (
              <Radio value={subDept.ItemClassID} key={subDept.ItemClassID}>
                <OverflowTip className="subdepartment">
                  {subDept.Name}
                </OverflowTip>
              </Radio>
            ))}
            {!subDepartmentsForGrabCategory?.length && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Radio.Group>
        </div>
      )}
      <div css={drawerFooter}>
        <Row gutter={12}>
          <Col span={6}>
            <Button
              data-testid="cancel-button"
              type="default"
              block
              onClick={handleCancelSubDepartmentDrawer}
            >
              Cancel
            </Button>
          </Col>
          <Col flex="auto">
            <Button
              data-testid="confirm-button"
              type="primary"
              block
              disabled={!selectedCategory || isRtkFlagLoading}
              onClick={handleSaveSubDepartmentDrawer}
            >
              Confirm
            </Button>
          </Col>
        </Row>
      </div>
    </Drawer>
  )
}
